import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, WifiOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useOnlineStatus } from '../hooks/useOnlineStatus';

/*
 * Decision 31: a clear "You're offline" banner when the connection drops, and a green "Back online"
 * note for a few seconds when it returns. role=status so screen readers hear the change.
 */
export function ConnectivityBanner({ className, uploadingDrafts = false }: { className?: string; uploadingDrafts?: boolean }) {
  const { t } = useTranslation('quotes');
  const online = useOnlineStatus();
  const [showBack, setShowBack] = useState(false);
  const wasOffline = useRef(false);

  useEffect(() => {
    if (!online) {
      wasOffline.current = true;
      setShowBack(false);
      return;
    }
    if (wasOffline.current) {
      wasOffline.current = false;
      setShowBack(true);
      const id = setTimeout(() => setShowBack(false), 4000);
      return () => clearTimeout(id);
    }
  }, [online]);

  if (online && !showBack) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      data-connectivity={online ? 'online' : 'offline'}
      className={cn(
        'flex items-center justify-center gap-2 px-4 py-2 text-center text-body-sm font-semibold',
        online ? 'bg-success-soft text-success-text' : 'bg-warn-soft text-warn-text',
        className,
      )}
    >
      {online ? <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden /> : <WifiOff className="h-4 w-4 shrink-0" aria-hidden />}
      <span>{online ? (uploadingDrafts ? t('offline.uploading') : t('offline.backOnline')) : t('offline.banner')}</span>
    </div>
  );
}
